.home { background: var(--paper); }

.header {
  padding: 44px 48px 34px;
  background: var(--panel);
  border-bottom: 1px solid var(--rule);
}
.headerTop {
  display: flex; flex-wrap: wrap; gap: 40px;
  justify-content: space-between; align-items: flex-end;
}
.title { margin: 12px 0 0; font: 300 52px/1.02 var(--serif); color: var(--ink); text-wrap: pretty; }
.blurb { margin: 14px 0 0; max-width: 58ch; font: 400 15px/1.7 var(--sans); color: var(--ink-68); text-wrap: pretty; }

.progress { flex: none; text-align: right; }
.progressLabel { font: 400 10px var(--mono); letter-spacing: .16em; color: var(--ink-45); }
.progressCount { font: 300 46px/1 var(--serif); color: var(--ink); margin-top: 6px; }
.progressCount span { font-size: 20px; color: var(--ink-45); }
.progressMeter { width: 180px; margin-top: 12px; }
.progressLeft { margin-top: 10px; font: 400 11px var(--mono); color: var(--ink-55); }

.actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-top: 26px; }
/* The reader's language track, shown not chosen (no picker until a second
   track ships). Quiet next to the button: a label, not a control. */
.trackTag {
  display: inline-flex; align-items: center; height: 28px; padding: 0 10px;
  border: 1px solid var(--rule); border-radius: 999px;
  font: 500 12px var(--sans); letter-spacing: 0.04em; color: var(--ink-55);
}

/* Unit bands ------------------------------------------------------------- */
.band {
  display: grid;
  grid-template-columns: 308px 1fr;
  gap: 40px;
  padding: 34px 48px;
  border-bottom: 1px solid var(--ink-12);
  background: var(--paper);
}
.band.locked { background: var(--panel-2); }
.band.active {
  background: var(--invert-bg);
  border-bottom-color: var(--invert-edge);
}

.bandNum { font: 300 42px/1 var(--serif); color: var(--ink-30); }
.band.done .bandNum { color: var(--olive); }
.band.active .bandNum { color: var(--invert-label); }

.bandName { margin: 8px 0 0; font: 300 27px/1.15 var(--serif); color: var(--ink); }
.bandMeta {
  margin-top: 7px; font: 400 10.5px var(--mono); letter-spacing: .12em;
  text-transform: uppercase; color: var(--ink-55);
}
.bandBlurb { margin: 14px 0 0; max-width: 34ch; font: 400 13px/1.65 var(--sans); color: var(--ink-55); }
.bandCount { margin-top: 14px; font: 400 11px var(--mono); color: var(--ink-45); }

.band.active .bandName { color: var(--invert-fg); }
.band.active .bandMeta { color: var(--invert-label); }
.band.active .bandBlurb { color: var(--invert-dim); }
.band.active .bandCount { color: var(--invert-faint); }

/* Lesson track -----------------------------------------------------------
   Was a wrapping grid of equal tiles, which said "seven things" where the
   content says "in this order". One column, one spine, and the checkpoint
   closing the week — which is what the content already does: the check is the
   last lesson in every week that has one. */
.track { position: relative; align-self: start; }
/* The spine is drawn per row rather than once down the container: the rows are
   not all the same height (the checkpoint carries extra padding and a margin),
   so a single absolute rule has to guess at its own end caps. A segment per row,
   trimmed to the node's centre at each end, lands correctly whatever the rows do. */
.row::before {
  content: ''; position: absolute; left: 13px; top: 0; bottom: 0;
  width: 1px; background: var(--ink-16);
}
.band.active .row::before { background: var(--invert-rule); }
.row:first-child::before { top: 50%; }
.row:last-child::before { bottom: 50%; }
.isCheck::before { top: -7px; }            /* bridge the gap its margin opens */

.row {
  position: relative; width: 100%;
  display: grid; grid-template-columns: 28px minmax(0, 1fr) auto auto;
  align-items: center; gap: 0 14px;
  padding: 13px 12px 13px 0;
  border: none; background: none; text-align: left; cursor: pointer;
  border-radius: var(--radius);
  transition: background-color .12s ease;
}
.row + .row { box-shadow: inset 0 1px 0 var(--ink-08); }
.band.active .row + .row { box-shadow: inset 0 1px 0 var(--invert-rule); }
.row:hover:not(.rowLocked) { background: var(--ink-08); }
.band.active .row:hover:not(.rowLocked) { background: var(--invert-surface); }
.rowLocked { opacity: .45; cursor: default; }

/* The node carries two things on two separate properties, which is the whole
   point: SHAPE is the kind of lesson — circle to read, triangle to practise,
   square to be assessed, star for an assignment — and FILL is completion. Nothing is filled yet,
   because nothing is tracked yet; when it is, a hollow shape becomes solid and
   the column reads as progress without any new vocabulary.

   Position is deliberately absent. An earlier pass filled the first node to
   say "begin here", which spent the completion signal on something a numbered
   list already says.

   The span is only a mask: it sits ON the spine and carries the band's ground
   so the rule does not run visibly through the shape. */
.node {
  justify-self: center; position: relative; z-index: 1;
  display: grid; place-items: center;
  width: 14px; height: 14px; border-radius: 50%;
  background: var(--paper);
}
.band.active .node { background: var(--invert-bg); }
.nodeMark {
  display: block; overflow: visible;
  fill: none; stroke: currentColor; stroke-width: 1.25; color: var(--ink-30);
}
.band.active .nodeMark { color: var(--invert-faint); }
/* The two that ask something of you take the band's gold. Read stays neutral —
   it is 71% of the course, and marking the default marks nothing. */
.isTrial .nodeMark, .isCheck .nodeMark, .isAssignment .nodeMark { color: var(--olive); }
.band.active .isTrial .nodeMark,
.band.active .isCheck .nodeMark,
.band.active .isAssignment .nodeMark { color: var(--invert-label); }

.rowTitle { font: 500 15px/1.4 var(--sans); color: var(--ink); }
.band.active .rowTitle { color: var(--invert-fg); }
.rowStatus { font: 500 9.5px var(--mono); letter-spacing: .12em; color: var(--accent); white-space: nowrap; }
.rowKind { font: 500 9.5px var(--mono); letter-spacing: .16em; color: var(--ink-45); white-space: nowrap; }
.band.active .rowKind { color: var(--invert-faint); }

/* practice — asks something of you, but nothing is recorded */
.isTrial .rowKind {
  padding: 2px 7px; border: 1px solid var(--gold-rule); border-radius: 3px; color: var(--olive);
}
.band.active .isTrial .rowKind { border-color: var(--invert-label); color: var(--invert-label); }

/* the checkpoint — the week's gate, and the reason the track has an end.
   Specificity note: `.row + .row` sets the hairline, so the stronger rule
   above the checkpoint needs `.row + .row.isCheck` to outrank it. */
.isCheck { margin-top: 7px; padding-top: 18px; }
.row + .row.isCheck { box-shadow: inset 0 1px 0 var(--ink-16); }
.band.active .row + .row.isCheck { box-shadow: inset 0 1px 0 var(--invert-rule); }
.isCheck .rowTitle { font-weight: 600; }
.isCheck .rowKind {
  padding: 2px 7px; border-radius: 3px;
  background: var(--olive); color: var(--paper); font-weight: 700;
}
.band.active .isCheck .rowKind { background: var(--invert-label); color: var(--invert-bg); }

/* done — only reachable once progress tracking is on. The shape does not
   change; it fills in, which is the one thing fill is kept for. */
.rowDone .nodeMark { fill: currentColor; }
.rowDone .rowTitle { color: var(--ink-55); }
.band.active .rowDone .rowTitle { color: var(--invert-dim); }
.rowStatus.done { color: var(--olive); }
.rowStatus.lockedText { color: var(--ink-30); }
.band.active .rowStatus.done { color: var(--invert-label); }
.band.active .rowStatus.lockedText { color: var(--invert-faint); }

/* On a phone the four columns will not fit, so the row becomes two lines:
   the title, then its metadata under it. */
@media (max-width: 720px) {
  .row {
    grid-template-columns: 24px minmax(0, 1fr) auto;
    grid-template-areas:
      "node title title"
      "node kind  status";
    row-gap: 5px; padding-right: 2px;
  }
  .node { grid-area: node; align-self: start; margin-top: 5px; }
  .rowTitle { grid-area: title; }
  .rowKind { grid-area: kind; justify-self: start; }
  .rowStatus { grid-area: status; justify-self: end; }
}

.footer {
  padding: 30px 48px; background: var(--panel);
  display: flex; flex-wrap: wrap; gap: 12px;
  justify-content: space-between; align-items: center;
  font: 400 12.5px var(--sans); color: var(--ink-55);
}

/* Narrow ----------------------------------------------------------------- */
@media (max-width: 860px) {
  .header { padding: 32px 22px 26px; }
  .title { font-size: 38px; }
  .progress { text-align: left; }
  .band { grid-template-columns: 1fr; gap: 22px; padding: 28px 22px; }
  .footer { padding: 24px 22px; }
}
